import {
  Menu,
  MenuButtonProps,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  SplitButton,
} from "@fluentui/react-components";
import {
  ChartMultipleRegular,
  CollectionsRegular,
  DataTrendingRegular,
  HistoryRegular,
  PeopleLockRegular,
  PeopleRegular,
  PeopleSettingsRegular,
  RibbonRegular,
} from "@fluentui/react-icons";
import { ExcelDocumentIcon } from "@fluentui/react-icons-mdl2-branded";
import { EditClient } from "components/EditClient/EditClient";
import { OverflowComponent } from "components/OverflowComponent/OverflowComponent";
import { StyledTab, StyledTabList, ToolbarContainer } from "components/styled";
import { helpAnchor } from "help/anchor";
import { useRoleType } from "hooks/useRoleType";
import { useAtom } from "jotai";
import { EManageHrTabView, ERole } from "library/types";
import { useTranslation } from "react-i18next";
import { useGetAuthorization } from "state/queries/useGetAuthorization";
import { selectedHrTabState } from "store";
import { selectedCompanyState } from "store/UIHrPage";
import styled from "styled-components";
import { ManageEmployee } from "../ManagePageView/EmployeesView/EmployeesTable/EmployeeActions/ManageEmployee/ManageEmployee";
import { EmployeeEnrollmentsReport } from "./components/EmployeeEnrollmentsReport";
import { EmployeeEnrollmentsReportByTimePeriod } from "./components/EmployeeEnrollmentsReportByTimePeriod";
import { GenerateEmployeeTemplate } from "./components/GenerateEmployeeTemplate";
import { HrImplementationOrderButton } from "./components/HrImplementationOrderButton";
import { ImportEmployeeTemplate } from "./components/ImportEmployeeTemplate";
import { ExportConsentData } from "./components/ExportConsentData";

const getTabs = ({
  t,
  canSeeUserAssignment,
  canSeeBulkActions,
  canSeeSecurity,
}) => [
  {
    value: EManageHrTabView.summary,
    name: t("hbh.summary.label"),
    icon: <ChartMultipleRegular />,
    help: helpAnchor("hr-tab-summary"),
  },
  {
    value: EManageHrTabView.benefits,
    name: t("hbh.benefits.label"),

    icon: <RibbonRegular />,
    help: helpAnchor("hr-tab-benefits"),
  },
  {
    value: EManageHrTabView.employees,
    name: t("hbh.employees.label"),

    icon: <PeopleRegular />,
    help: helpAnchor("hr-tab-employees"),
  },

  ...(canSeeBulkActions
    ? [
        {
          value: EManageHrTabView.bulk,
          name: t("hbh.bulkManagement.label"),
          icon: <CollectionsRegular />,
          help: helpAnchor("hr-tab-bulk"),
        },
      ]
    : []),
  {
    value: EManageHrTabView.summaryPbi,
    name: t("hbh.analytics.label"),

    icon: <DataTrendingRegular />,
    help: helpAnchor("hr-tab-analytics"),
  },
  {
    value: EManageHrTabView.actionHistory,
    name: t("hbh.actionHistory.label"),

    icon: <HistoryRegular />,
    help: helpAnchor("hr-tab-action-history"),
  },
  ...(canSeeUserAssignment
    ? [
        {
          value: EManageHrTabView.userAssignments,
          name: t("hbh.userAssignments.label"),

          icon: <PeopleSettingsRegular />,
          help: helpAnchor("hr-tab-user-assignments"),
        },
      ]
    : []),
  ...(canSeeSecurity
    ? [
        {
          value: EManageHrTabView.security,
          name: t("hbh.security.label"),

          icon: <PeopleLockRegular />,
          help: helpAnchor("hr-tab-security"),
        },
      ]
    : []),
];

const canSeeUserAssignmentRoles = [
  ERole.SuperAdmin,
  ERole.GlobalAdmin,
  ERole.ReadonlyGlobalAdmin,
  ERole.CountryAdmin,
  ERole.ReadonlyCountryAdmin,
  ERole.CompanyAdmin,
  ERole.ReadonlyCompanyAdmin,
];

export function HrPageToolbar() {
  const { t } = useTranslation();
  const { data: authorization } = useGetAuthorization();
  const isAdminUser = useRoleType()?.hasAdminRole;
  const isReadOnly = useRoleType()?.hasHrReadOnly;
  const isCompanyAdminRole = useRoleType()?.hasCompanyAdminRole;
  const canSeeUserAssignment = authorization?.roles?.some((role) =>
    canSeeUserAssignmentRoles.includes(role as ERole)
  );

  const canWriteUserAssignment = authorization?.isHRCompanyAdminAdmin;
  const canReadUserAssignment =
    canWriteUserAssignment || authorization?.isHRCompanyAdminReadOnlyAdmin;

  const canSeeBulkActions = isAdminUser;
  const canSeeSecurity =
    isAdminUser ||
    (isCompanyAdminRole && (canReadUserAssignment || canWriteUserAssignment));

  const [selectedTab, setSelectedTab] = useAtom(selectedHrTabState);

  const hasActions =
    selectedTab === EManageHrTabView.employees ||
    selectedTab === EManageHrTabView.bulk;

  const employeeTabActive = selectedTab === EManageHrTabView.employees;

  const showSaveReorderButton = selectedTab === EManageHrTabView.benefits;
  const tabs = getTabs({
    t,
    canSeeUserAssignment,
    canSeeBulkActions,
    canSeeSecurity,
  });

  return (
    <ToolbarContainer $mobileTop={employeeTabActive ? "90px" : ""}>
      <StyledTabList
        selectedValue={selectedTab}
        onTabSelect={(e, data) => {
          setSelectedTab(data.value as EManageHrTabView);
        }}
        style={{
          overflowX: "auto",
        }}
      >
        <OverflowComponent>
          {tabs.map((tab) => {
            return (
              <StyledTab
                key={tab.value}
                data-help={tab.help}
                icon={tab.icon}
                value={tab.value}
              >
                {tab.name}
              </StyledTab>
            );
          })}
        </OverflowComponent>
      </StyledTabList>

      <ActionsWrap>
        {showSaveReorderButton && <HrImplementationOrderButton />}
        {canSeeBulkActions && <EditClient companyAtom={selectedCompanyState} />}

        {hasActions && (
          <>
            <Menu positioning="below-end" persistOnItemClick>
              <MenuTrigger disableButtonEnhancement>
                {(triggerProps: MenuButtonProps) => (
                  <SplitButton
                    data-help="hr-actions-menu"
                    icon={<ExcelDocumentIcon />}
                    appearance="transparent"
                    menuButton={triggerProps}
                  >
                    <span onClick={triggerProps.onClick}>
                      {t("hbh.actions.label")}
                    </span>
                  </SplitButton>
                )}
              </MenuTrigger>

              <StyledPopover>
                <StyledMenuList>
                  <StyledMenuItem>
                    <GenerateEmployeeTemplate />
                  </StyledMenuItem>
                  <StyledMenuItem>
                    <EmployeeEnrollmentsReport />
                  </StyledMenuItem>
                  <StyledMenuItem>
                    <EmployeeEnrollmentsReportByTimePeriod />
                  </StyledMenuItem>
                  <MenuItem>
                    <ImportEmployeeTemplate />
                  </MenuItem>
                  <MenuItem>
                    <ExportConsentData />
                  </MenuItem>
                </StyledMenuList>
              </StyledPopover>
            </Menu>
            <div>
              <ManageEmployee isAddEmployee disabled={isReadOnly} />
            </div>
          </>
        )}
      </ActionsWrap>
    </ToolbarContainer>
  );
}

const ActionsWrap = styled.div`
  display: flex;
  align-items: center;

  gap: 10px;
  justify-content: flex-end;
  flex: 1;
  .fui-SplitButton__primaryActionButton {
    color: ${({ theme }) => theme.palette.purpleDark};
  }
`;

const StyledPopover = styled(MenuPopover)`
  max-width: max-content;
  min-width: max-content;
  width: max-content;
`;
const StyledMenuList = styled(MenuList)`
  max-width: max-content;
  min-width: max-content;
  width: max-content;
`;

const StyledMenuItem = styled(MenuItem)`
  max-width: max-content;
  min-width: max-content;
  width: max-content;
`;
